// A report for one range and grouping: the numbers behind the screen, the printed report and the CSV.
// A port of the Mac's ReportModel (apple/Tally/Views/ReportsView.swift).
import {
  bucketSizeFor, reportBuckets, reportGrouped, reportPerProject, reportTotals, unassignedMs, type BucketSize, type Interval,
  type ProjectTotals, type ReportItem, type ReportTotals,
} from '../../../packages/core/src/index.ts';
import type { Translate } from '../lib/i18n.ts';
import { addUsage, usageSummary, type UsageSummary } from './session.ts';
import { billingProjects, client, memberOfDevice, project, providerInfo, reportItems, type RangeData, type Workspace } from './data.ts';

export type ReportGrouping = 'project' | 'client' | 'source' | 'provider' | 'billable' | 'member';
export const REPORT_GROUPINGS: ReportGrouping[] = ['project', 'client', 'source', 'provider', 'billable'];
/** With the team's time (owners in a team) reports also group by member. */
export const TEAM_GROUPINGS: ReportGrouping[] = [...REPORT_GROUPINGS, 'member'];

/** Groups that split projects (sources, providers) can overlap each other; the others partition the projects. */
export const groupingOverlaps = (g: ReportGrouping): boolean => g === 'source' || g === 'provider';

export interface ReportRow {
  key: string;
  label: string;
  slot: number | null;
  source: 'human' | 'agent' | null;
  totals: ReportTotals;
  usage: UsageSummary | null;
}

export interface ReportPoint {
  bucket: Interval;
  key: string;
  label: string;
  billedMs: number;
}

export interface ReportModel {
  range: Interval;
  grouping: ReportGrouping;
  total: ReportTotals;
  rows: ReportRow[];
  points: ReportPoint[];
  bucketSize: BucketSize;
  buckets: Interval[];
  unassignedMs: number;
  perProject: Map<string, ProjectTotals>;
  usage: UsageSummary | null;
}

export function groupingLabel(t: Translate, g: ReportGrouping): string {
  switch (g) {
    case 'project': return t('Projekt');
    case 'client': return t('Kunde');
    case 'source': return t('Quelle');
    case 'provider': return t('Provider');
    case 'billable': return t('Abrechenbar');
    case 'member': return t('Mitglied');
  }
}

export function rowLabel(t: Translate, ws: Workspace, key: string, g: ReportGrouping): string {
  switch (g) {
    case 'project': return project(ws, key)?.name ?? t('Gelöschtes Projekt');
    case 'client': return key === '-' ? t('Ohne Kunde') : client(ws, key)?.name ?? t('Gelöschter Kunde');
    case 'source': return key === 'human' ? t('Eigene Zeit') : t('Agents');
    case 'provider': return key === 'human' ? t('Eigene Zeit') : providerInfo(key).name;
    case 'billable': return key === 'billable' ? t('Abrechenbar') : t('Intern');
    case 'member': {
      const member = ws.members.find((m) => m.id === key);
      return member && member.name && !member.removed ? member.name : member?.name || t('Ehemaliges Mitglied');
    }
  }
}

export function buildReport(t: Translate, ws: Workspace, data: RangeData, range: Interval, grouping: ReportGrouping, now: number, timeZone: string, withUsage: boolean): ReportModel {
  const items = reportItems(data, now);
  const projects = billingProjects(ws);
  const byProject = new Map(ws.projects.map((p) => [p.id, p]));
  const key = (item: ReportItem): string | null => {
    const p = item.project ? byProject.get(item.project) : undefined;
    if (!item.project || !p) return null;
    switch (grouping) {
      case 'project': return item.project;
      case 'client': return p.clientId ?? '-';
      case 'source': return item.source;
      case 'provider': return item.source === 'human' ? 'human' : item.provider ?? 'agent';
      case 'billable': return p.billable && (item.source === 'agent' || item.billable) ? 'billable' : 'internal';
      case 'member': return item.member ?? '-';
    }
  };
  const grouped = reportGrouped(items, projects, range, now, key);
  const rows: ReportRow[] = [...grouped].map(([k, totals]): ReportRow => ({
    key: k,
    label: rowLabel(t, ws, k, grouping),
    slot: grouping === 'project' ? byProject.get(k)?.colorSlot ?? null : null,
    source: grouping === 'source' || grouping === 'provider' ? (k === 'human' ? 'human' : 'agent') : null,
    totals,
    usage: null,
  })).sort((a, b) => b.totals.billedMs - a.totals.billedMs);

  const bucketSize = bucketSizeFor(range);
  const buckets = reportBuckets(range, bucketSize, timeZone);
  const labels = new Map(rows.map((r) => [r.key, r.label]));
  // Stable stacking: the largest group at the bottom, in the same order every time.
  const order = new Map(rows.map((r, i) => [r.key, i]));
  const points: ReportPoint[] = [];
  for (const bucket of buckets) {
    if (bucket.start > now) continue;
    const g = [...reportGrouped(items, projects, bucket, now, key)].sort((a, b) => (order.get(a[0]) ?? 1e9) - (order.get(b[0]) ?? 1e9));
    for (const [k, totals] of g) points.push({ bucket, key: k, label: labels.get(k) ?? k, billedMs: totals.billedMs });
  }

  let usage: UsageSummary | null = null;
  if (withUsage) {
    // Tokens belong to sessions: they follow the session's own project.
    const sessions = new Map(data.sessions.map((s) => [s.id, s]));
    const perRow = new Map<string, UsageSummary>();
    let all = usageSummary([]);
    for (const [sessionId, records] of data.usage) {
      const session = sessions.get(sessionId);
      if (!session) continue;
      const summary = usageSummary(records);
      all = addUsage(all, summary);
      const p = session.projectId ? byProject.get(session.projectId) : undefined;
      const rowKey = grouping === 'project' ? session.projectId
        : grouping === 'client' ? (p ? p.clientId ?? '-' : null)
        : grouping === 'source' ? 'agent'
        : grouping === 'provider' ? session.provider
        : grouping === 'member' ? (memberOfDevice(data, session.deviceId) ?? '-')
        : p ? (p.billable ? 'billable' : 'internal') : null;
      if (rowKey) perRow.set(rowKey, perRow.has(rowKey) ? addUsage(perRow.get(rowKey) as UsageSummary, summary) : summary);
    }
    usage = all;
    for (const row of rows) row.usage = perRow.get(row.key) ?? null;
  }

  return {
    range, grouping, rows, points, bucketSize, buckets, usage,
    total: reportTotals(items, projects, range, now),
    unassignedMs: unassignedMs(items, range, now),
    perProject: reportPerProject(items, projects, range, now),
  };
}

export type ReportPeriod = 'thisWeek' | 'lastWeek' | 'thisMonth' | 'lastMonth' | 'thisQuarter' | 'thisYear' | 'custom';
export const REPORT_PERIODS: ReportPeriod[] = ['thisWeek', 'lastWeek', 'thisMonth', 'lastMonth', 'thisQuarter', 'thisYear', 'custom'];

export function periodLabel(t: Translate, p: ReportPeriod): string {
  switch (p) {
    case 'thisWeek': return t('Diese Woche');
    case 'lastWeek': return t('Letzte Woche');
    case 'thisMonth': return t('Dieser Monat');
    case 'lastMonth': return t('Letzter Monat');
    case 'thisQuarter': return t('Dieses Quartal');
    case 'thisYear': return t('Dieses Jahr');
    case 'custom': return t('Zeitraum wählen');
  }
}
